import * as React from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, Clock, Search } from 'lucide-react';
import { VendorConfidentialChip, VendorSourceChip } from '@/components/ui/citation-chip';
import { SearchField } from '@/components/ui/workspace';
import {
  useKbItems,
  type ItemListParams,
  type KbItemRow,
  type KbProduct,
  type KbSummary,
  type KbSummaryModule,
  type KbVendor,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Count, Empty, KindChip, MonoNo, Quote, Section, SectionHead, SubHead } from '../kb-bits';
import { KNOWLEDGE_KINDS, formatDate, plural } from '../kb-logic';
import { useKb } from '../vendor-knowledge-screen';
import { ItemDetail } from './item-detail';

/**
 * Knowledge (prototype `vKnowledge()`; E13-F02 §3): a vendor's confirmed
 * items, product by product, by module and process area as the server
 * orders them, each opening in place. Lists are virtualised against the
 * page's own scroll and fetched a page at a time (cursor paging), so a
 * product with thousands of items stays responsive. Module counts are the
 * server's (`GET /summary` modules).
 */
export function KnowledgeSection({ vendor, summary }: { vendor: KbVendor; summary: KbSummary }) {
  const { canCurate } = useKb();
  const [typed, setTyped] = React.useState('');
  const q = useDebounced(typed.trim(), 250);
  const [closed, setClosed] = React.useState<Set<string>>(new Set());
  const [openItem, setOpenItem] = React.useState<string | null>(null);
  const modules = summary.modules ?? [];
  const withItems = (productId: string | null) =>
    modules.some((m) => m.product_id === productId && m.knowledge_items > 0);
  const products = vendor.products.filter((p) => withItems(p.id));
  const company = withItems(null);
  const lists: Array<{ key: string; product: KbProduct | null; params: ItemListParams }> = [
    ...products.map((p) => ({
      key: p.id,
      product: p,
      params: { product_id: p.id, kind: KNOWLEDGE_KINDS, status: 'confirmed', q: q || undefined },
    })),
    ...(company
      ? [
          {
            key: 'company',
            product: null,
            params: {
              vendor_id: vendor.vendor.id,
              kind: 'company',
              status: 'confirmed',
              q: q || undefined,
            },
          },
        ]
      : []),
  ];
  const several = lists.length > 1;
  const toggle = (key: string) =>
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Section id="kb-knowledge" label="Knowledge">
      <SectionHead
        title="Knowledge"
        count={summary.knowledge_items}
        actions={
          <SearchField
            value={typed}
            onChange={setTyped}
            aria-label="Search this vendor’s knowledge"
            placeholder="Search this vendor’s knowledge"
            icon={<Search className="h-3.5 w-3.5" aria-hidden="true" />}
            wrapperClassName="w-[280px] max-w-full"
          />
        }
      />
      {summary.knowledge_items === 0 ? (
        <Empty>Nothing confirmed yet.</Empty>
      ) : (
        lists.map((l) => (
          <div key={l.key}>
            {several ? (
              <SubHead
                count={
                  q
                    ? undefined
                    : modules
                        .filter((m) => m.product_id === (l.product?.id ?? null))
                        .reduce((n, m) => n + m.knowledge_items, 0)
                }
              >
                {l.product?.name ?? 'Company, all products'}
              </SubHead>
            ) : null}
            <ItemList
              params={l.params}
              productName={l.product?.name ?? 'All products'}
              modules={modules.filter((m) => m.product_id === (l.product?.id ?? null))}
              searching={!!q}
              query={q}
              closed={closed}
              onToggle={toggle}
              openItem={openItem}
              onOpenItem={setOpenItem}
            />
          </div>
        ))
      )}
      {canCurate && (summary.kept_items ?? 0) > 0 ? (
        <div className="mt-1 text-meta text-ink-500">
          {plural(summary.kept_items ?? 0, 'retired or replaced item')}{' '}
          {summary.kept_items === 1 ? 'is' : 'are'} kept on record. Nothing is ever deleted.
        </div>
      ) : null}
    </Section>
  );
}

export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

type Row =
  | { type: 'header'; key: string; label: string; count: number | null; first: boolean }
  | { type: 'item'; key: string; item: KbItemRow; last: boolean }
  | { type: 'more'; key: string };

/** The module an item is filed under: a library module, or the label typed outside it. */
export function moduleOf(item: KbItemRow): { key: string; label: string } {
  if (item.module?.id) return { key: `m:${item.module.id}`, label: item.module.name ?? 'Module' };
  if (item.module?.label) {
    return { key: `l:${item.module.label.trim().toLowerCase()}`, label: item.module.label };
  }
  return { key: 'none', label: 'Not filed under a module' };
}

function countOf(modules: KbSummaryModule[], key: string): number | null {
  const m = modules.find((x) =>
    key.startsWith('m:')
      ? x.library_module_id === key.slice(2)
      : key.startsWith('l:')
        ? !x.library_module_id && (x.label ?? '').trim().toLowerCase() === key.slice(2)
        : !x.library_module_id && !x.label,
  );
  return m ? m.knowledge_items : null;
}

function ItemList({
  params,
  productName,
  modules,
  searching,
  query,
  closed,
  onToggle,
  openItem,
  onOpenItem,
}: {
  params: ItemListParams;
  productName: string;
  modules: KbSummaryModule[];
  searching: boolean;
  query: string;
  closed: Set<string>;
  onToggle: (key: string) => void;
  openItem: string | null;
  onOpenItem: (id: string | null) => void;
}) {
  const { scrollElement } = useKb();
  const items = useKbItems(params);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [offset, setOffset] = React.useState(0);

  const all = React.useMemo(() => items.data?.pages.flatMap((p) => p.items) ?? [], [items.data]);
  const rows = React.useMemo(() => {
    const out: Row[] = [];
    let prev: string | null = null;
    all.forEach((item) => {
      const m = moduleOf(item);
      if (m.key !== prev) {
        const last = out[out.length - 1];
        if (last?.type === 'item') last.last = true;
        out.push({
          type: 'header',
          key: `h:${m.key}`,
          label: m.label,
          count: searching ? null : countOf(modules, m.key),
          first: prev === null,
        });
        prev = m.key;
      }
      if (searching || !closed.has(`${params.product_id ?? 'company'}:${m.key}`)) {
        out.push({ type: 'item', key: item.id, item, last: false });
      }
    });
    const last = out[out.length - 1];
    if (last?.type === 'item') last.last = true;
    if (items.hasNextPage) out.push({ type: 'more', key: 'more' });
    return out;
  }, [all, closed, modules, searching, items.hasNextPage, params.product_id]);

  // Where this list starts inside the page's scroll, kept current as the page above it changes.
  React.useLayoutEffect(() => {
    const scroller = scrollElement();
    const el = listRef.current;
    if (!scroller || !el) return;
    const measure = () => {
      const top =
        el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      setOffset((o) => (Math.abs(o - top) > 0.5 ? top : o));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    if (scroller.firstElementChild) ro.observe(scroller.firstElementChild);
    return () => ro.disconnect();
  }, [scrollElement]);

  const virtualizer = useVirtualizer<HTMLElement, HTMLDivElement>({
    count: rows.length,
    getScrollElement: scrollElement,
    estimateSize: (i) => {
      const r = rows[i];
      if (!r || r.type === 'header') return 44;
      if (r.type === 'more') return 40;
      return r.item.id === openItem ? 420 : 104;
    },
    getItemKey: (i) => rows[i]?.key ?? i,
    overscan: 10,
    scrollMargin: offset,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = items;
  React.useEffect(() => {
    if (lastIndex >= rows.length - 1 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [lastIndex, rows.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (items.isPending) {
    return <div className="py-3 text-meta text-ink-500">Loading items…</div>;
  }
  if (all.length === 0) {
    return (
      <Empty className="mb-2.5">
        {searching ? `Nothing matches “${query}”.` : 'Nothing confirmed yet.'}
      </Empty>
    );
  }

  const groupKey = (key: string) => `${params.product_id ?? 'company'}:${key.slice(2)}`;

  return (
    <div ref={listRef} className="relative mb-2.5" style={{ height: virtualizer.getTotalSize() }}>
      {virtualItems.map((v) => {
        const row = rows[v.index]!;
        return (
          <div
            key={v.key}
            ref={virtualizer.measureElement}
            data-index={v.index}
            className="absolute left-0 top-0 w-full"
            style={{ transform: `translateY(${v.start - virtualizer.options.scrollMargin}px)` }}
          >
            {row.type === 'header' ? (
              <ModuleHead
                row={row}
                productName={productName}
                open={searching || !closed.has(groupKey(row.key))}
                onToggle={() => onToggle(groupKey(row.key))}
              />
            ) : row.type === 'item' ? (
              <ItemRow
                item={row.item}
                last={row.last}
                open={openItem === row.item.id}
                onToggle={() => onOpenItem(openItem === row.item.id ? null : row.item.id)}
              />
            ) : (
              <div className="py-2.5 text-meta text-ink-500">Loading more…</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ModuleHead({
  row,
  productName,
  open,
  onToggle,
}: {
  row: Extract<Row, { type: 'header' }>;
  productName: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={cn(!row.first && 'pt-2.5')}>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-2.5 border border-ink-100 bg-paper-50 px-3.5 py-2.5 text-left text-body-sm hover:bg-paper-100',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent-amber-600',
          open ? 'rounded-t-card border-b-0' : 'rounded-card',
        )}
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 flex-none text-ink-300 transition-transform',
            !open && '-rotate-90',
          )}
          aria-hidden="true"
        />
        <span className="font-semibold text-ink-900">{row.label}</span>
        <span className="text-meta text-ink-500">{productName}</span>
        <span className="flex-1" />
        {row.count !== null ? <Count>{row.count}</Count> : null}
      </button>
    </div>
  );
}

function ItemRow({
  item,
  last,
  open,
  onToggle,
}: {
  item: KbItemRow;
  last: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={cn(
        'border-x border-t border-ink-100 bg-paper-0',
        last && 'rounded-b-card border-b',
        open && 'shadow-[inset_3px_0_0_var(--accent-amber-600)]',
      )}
    >
      <div
        className={cn(
          'flex w-full cursor-pointer items-start gap-3 px-3.5 py-3 hover:bg-paper-50',
          open && 'bg-paper-50',
        )}
        onClick={onToggle}
      >
        <div className="min-w-0 flex-1">
          <button
            type="button"
            aria-expanded={open}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="block w-full rounded-sm border-0 bg-transparent p-0 text-left text-body font-medium text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600"
          >
            {item.statement}
          </button>
          {open ? null : <Quote text={item.excerpt} variant="clamp" as="div" />}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <KindChip kind={item.kind} />
            <VendorSourceChip kind={item.source_kind} />
            {item.process_area ? (
              <span className="text-meta text-ink-500">{item.process_area}</span>
            ) : null}
            <span className="text-meta text-ink-500">as of {formatDate(item.as_of)}</span>
            {item.due_for_review ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium leading-4 text-posture-at-risk">
                <Clock className="h-[11px] w-[11px]" aria-hidden="true" /> Due for review
              </span>
            ) : null}
            {item.confidential ? <VendorConfidentialChip /> : null}
            <span className="flex-1" />
            {item.number ? <MonoNo>{item.number}</MonoNo> : null}
          </div>
        </div>
        <ChevronDown
          className={cn('mt-[3px] h-3.5 w-3.5 flex-none text-ink-300', open && 'rotate-180')}
          aria-hidden="true"
        />
      </div>
      {open ? <ItemDetail itemId={item.id} /> : null}
    </div>
  );
}
